import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { Role } from '../../models/user.model';
import { DatadogService } from '../../services/datadog.service';
import { LocalStorageService } from '../../services/localStorage.service';
import { UsersService } from '../../services/users.service';
import { ClicktermPromoPlacement, buildClicktermPromoUrl } from './clickterm-promo-url';

export const CLICKTERM_PROMO_DISMISSAL_KEY = 'clickterm-promo-banner-dismissed-at';
export const CLICKTERM_PROMO_DISMISSAL_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

@Component({
  standalone: false,
  selector: 'app-clickterm-promo-banner',
  templateUrl: './clickterm-promo-banner.component.html',
  styleUrls: ['./clickterm-promo-banner.component.scss'],
})
export class ClicktermPromoBannerComponent implements OnInit {
  private readonly storage = inject(LocalStorageService);
  private readonly usersService = inject(UsersService);
  private readonly datadogService = inject(DatadogService);

  /** Set a placement when a second banner ships, so clicks stay attributable per surface. */
  @Input() placement: ClicktermPromoPlacement | null = null;

  readonly isDismissed = signal(false);
  readonly isEligible = signal(false);

  readonly promoUrl = computed(() => buildClicktermPromoUrl(this.placement));
  readonly isVisible = computed(() => environment.clicktermPromoBannerEnabled && this.isEligible() && !this.isDismissed());

  ngOnInit(): void {
    this.isDismissed.set(this.hasActiveDismissal());
    this.isEligible.set(this.isPromotableUser());

    if (this.isVisible()) {
      this.datadogService.trackAction('clickterm_promo_banner_viewed', this.trackingContext());
    }
  }

  onPromoClick(): void {
    this.datadogService.trackAction('clickterm_promo_banner_clicked', this.trackingContext());
    this.suppress();
  }

  onDismiss(): void {
    this.suppress();
    this.datadogService.trackAction('clickterm_promo_banner_dismissed', this.trackingContext());
  }

  /**
   * A click counts as a dismissal: the user has taken the pitch, so showing the banner again on
   * their next dashboard visit would only nag. Hiding it mid-click does not cancel the navigation,
   * because an anchor still follows its hyperlink once detached from the document.
   */
  private suppress(): void {
    this.storage.set(CLICKTERM_PROMO_DISMISSAL_KEY, Date.now());
    this.isDismissed.set(true);
  }

  private trackingContext(): Record<string, unknown> {
    return { placement: this.placement ?? 'default', url: this.promoUrl() };
  }

  private hasActiveDismissal(): boolean {
    const dismissedAt = Number(this.storage.get(CLICKTERM_PROMO_DISMISSAL_KEY));

    if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) {
      return false;
    }

    const elapsed = Date.now() - dismissedAt;

    // A backwards clock change would otherwise hide the banner far past the 7 day window.
    if (elapsed < 0) {
      return false;
    }

    return elapsed < CLICKTERM_PROMO_DISMISSAL_WINDOW_MS;
  }

  private isPromotableUser(): boolean {
    const user = this.usersService.authUser;

    // Admins are internal staff, so the promo would only be noise for them.
    return !!user && user.role !== Role.ADMIN;
  }
}
